import { useSearchParams } from "react-router-dom";

import { linkTo } from "../api/links";
import { useOutbound } from "../api/queries";
import type { Outbound } from "../api/types";
import { LoadMore } from "../components/MasterDetail";
import {
  Badge,
  Empty,
  ErrorBox,
  Guid,
  Json,
  Num,
  Panel,
  SEQ,
  Spinner,
  StatusBadge,
  Time,
  shorten,
} from "../components/primitives";

const STATUSES = ["", "pending", "sent"];
const WINDOWS = [
  { value: "24", label: "24h" },
  { value: "168", label: "7d" },
  { value: "720", label: "30d" },
  { value: "2160", label: "90d" },
];

/**
 * The CTMS publish queue. A `pending` row is a message that has not left: the per-root drain
 * publishes oldest-seq-first and re-raises on failure, so one stuck head holds its whole root
 * behind it — which is exactly what "the dispatcher never saw it" looks like from here.
 */
export function OutboundPage() {
  const [params, setParams] = useSearchParams();
  const filters = {
    status: params.get("status") ?? undefined,
    root_guid: params.get("root_guid") ?? undefined,
    window_hours: Number(params.get("window_hours") ?? 168),
    limit: 50,
  };

  const query = useOutbound(filters);
  const messages = query.data?.pages.flatMap((page) => page.outbound) ?? [];

  function update(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  return (
    <div className="h-full space-y-4 overflow-y-auto p-5">
      <Panel
        id="outbound"
        title={`Outbound messages (${messages.length}${query.hasNextPage ? "+" : ""})`}
        actions={
          <div className="flex items-center gap-2">
            {filters.root_guid && (
              <button type="button" className="btn mono" onClick={() => update("root_guid", "")}>
                root: {shorten(filters.root_guid)} ✕
              </button>
            )}
            <select
              className="field-select"
              value={filters.status ?? ""}
              onChange={(event) => update("status", event.target.value)}
            >
              {STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status || "any status"}
                </option>
              ))}
            </select>
            <select
              className="field-select"
              value={String(filters.window_hours)}
              onChange={(event) => update("window_hours", event.target.value)}
              disabled={Boolean(filters.root_guid)}
              title={
                filters.root_guid
                  ? "A root filter reads the full retention, not a window"
                  : undefined
              }
            >
              {WINDOWS.map((window) => (
                <option key={window.value} value={window.value}>
                  {window.label}
                </option>
              ))}
            </select>
          </div>
        }
      >
        {query.isPending && <Spinner />}
        {query.error && <ErrorBox error={query.error} />}
        {query.isSuccess && messages.length === 0 && (
          <Empty>
            Nothing staged in this window.
            <div className="mt-1 text-xs text-faint">
              Mobile-only events stage nothing — only events with an outbound action reach CTMS.
            </div>
          </Empty>
        )}

        {messages.map((message) => (
          <OutboundRow key={message.id} message={message} />
        ))}

        <LoadMore
          hasNext={query.hasNextPage}
          loading={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
          label="Load older"
        />
      </Panel>
    </div>
  );
}

export function OutboundRow({ message }: { message: Outbound }) {
  return (
    <div className="row row-hover">
      <div className="flex flex-wrap items-center gap-3">
        <StatusBadge status={message.status} />
        <span className="mono text-faint">#{message.id}</span>
        {message.seq !== null && (
          <Num label="publish order" value={message.seq} hint={SEQ.publishOrder} />
        )}
        <span className="text-sm text-strong">{message.routing_key}</span>
        {message.attempts > 0 && <Badge tone="warn">{message.attempts} attempts</Badge>}
        {message.is_restricted && <Badge tone="warn">restricted</Badge>}
        <span className="flex items-center gap-1 text-xs">
          <span className="label">{message.root_type}</span>
          <Guid value={message.root_guid} to={linkTo.root(message.root_type, message.root_guid)} />
        </span>
        {message.event_guid && (
          <span className="flex items-center gap-1 text-xs">
            <span className="label">from event</span>
            <Guid value={message.event_guid} to={linkTo.event(message.event_guid)} />
          </span>
        )}
        <span className="ml-auto flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1">
            <span className="label">staged</span>
            <Time value={message.created_at} />
          </span>
          <span className="flex items-center gap-1">
            <span className="label">sent</span>
            <Time value={message.sent_at} />
          </span>
        </span>
      </div>

      {message.last_error && (
        <p className="mono mt-1.5 truncate text-bad/90" title={message.last_error}>
          {message.last_error}
        </p>
      )}

      <div className="mt-2">
        <Json value={message.envelope} collapsed />
      </div>
    </div>
  );
}
